import { useCallback, useEffect } from "react";
import type { ConfigMode } from "../Context/types";
import { FormData, ExistingProvider, ProviderFormState } from "../types";
import {
  normalizeBrandsArray,
  parseMccValue,
} from "../utils/providerDataTransformers";

const FORM_EDIT_INITIALIZATION_DELAY_MS = 30;
const FORM_ADD_INITIALIZATION_DELAY_MS = 100;

interface UseProviderFormInitializationProps {
  state: ProviderFormState;
  mode: ConfigMode;
  existingProvider?: ExistingProvider | null;
  providers: { id: string }[];
  priorityOptions: number[];
  merchantInfo?: { categoryCode: string } | null;
}

export function useProviderFormInitialization({
  state,
  mode,
  existingProvider,
  providers,
  priorityOptions,
  merchantInfo,
}: UseProviderFormInitializationProps): void {
  const { reset, setValue, formStateRef } = state;

  const resetFormForEdit = useCallback(() => {
    const providerToUse = existingProvider;
    if (
      !providerToUse ||
      providers.length === 0 ||
      priorityOptions.length === 0
    ) {
      return;
    }

    const currentProviderId = providerToUse.id;
    if (formStateRef.current.lastExistingProviderId === currentProviderId) {
      return;
    }

    formStateRef.current.lastExistingProviderId = currentProviderId;
    formStateRef.current.isInitializing = true;

    const brandsArray = normalizeBrandsArray(providerToUse.brands);
    const priorityValue = Number(providerToUse.priority);
    const providerValue = providerToUse.provider?.id || "";
    const validProvider = providers.some((p) => p.id === providerValue)
      ? providerValue
      : providers[0]?.id || "";
    const credentialsData = providerToUse.credentials
      ? { ...providerToUse.credentials }
      : {};
    const merchantIdValue = providerToUse?.merchantId || "";

    reset({
      provider: validProvider,
      priority: priorityValue,
      merchantId: merchantIdValue,
      mcc: parseMccValue(providerToUse.mcc),
      isActive: providerToUse.isActive ?? true,
      merchantKey: providerToUse.merchantKey || "",
      brands: brandsArray,
      credentials: credentialsData,
    });

    if (merchantIdValue) {
      setValue("merchantId", merchantIdValue, { shouldValidate: false });
    }

    if (providerToUse.credentials) {
      Object.keys(providerToUse.credentials).forEach((key) => {
        if (key !== "type") {
          setValue(
            `credentials.${key}` as keyof FormData,
            providerToUse.credentials?.[key] || "",
            { shouldValidate: false },
          );
        }
      });
    }

    setTimeout(() => {
      setValue("brands", brandsArray, { shouldValidate: false });
      formStateRef.current.isInitializing = false;
      formStateRef.current.previousSelectedProviderId = validProvider;
    }, FORM_EDIT_INITIALIZATION_DELAY_MS);
  }, [
    existingProvider,
    providers,
    priorityOptions,
    reset,
    setValue,
    formStateRef,
  ]);

  const resetFormForAdd = useCallback(() => {
    const categoryCode = merchantInfo
      ? Number(merchantInfo.categoryCode)
      : undefined;
    const mccValue =
      categoryCode && categoryCode !== 0 ? categoryCode : undefined;

    formStateRef.current.isInitializing = true;
    reset({
      provider: "",
      priority: undefined,
      merchantId: "",
      mcc: mccValue,
      isActive: true,
      merchantKey: "",
      brands: [],
      credentials: {},
    });
    setTimeout(() => {
      formStateRef.current.isInitializing = false;
      formStateRef.current.previousSelectedProviderId = null;
      formStateRef.current.hasAddModeInitialized = true;
    }, FORM_ADD_INITIALIZATION_DELAY_MS);
  }, [merchantInfo, reset, formStateRef]);

  useEffect(() => {
    if (mode === "EDIT") {
      if (existingProvider) {
        const currentProviderId = existingProvider.id;
        if (formStateRef.current.lastExistingProviderId !== currentProviderId) {
          if (providers.length > 0 && priorityOptions.length > 0) {
            resetFormForEdit();
          }
        }
      } else {
        formStateRef.current.lastExistingProviderId = null;
      }
    } else if (mode === "ADD") {
      if (!formStateRef.current.hasAddModeInitialized) {
        resetFormForAdd();
      }
      formStateRef.current.lastExistingProviderId = null;
    }
  }, [
    mode,
    existingProvider,
    providers.length,
    priorityOptions.length,
    resetFormForEdit,
    resetFormForAdd,
    formStateRef,
  ]);
}
